<template>
  <div class="dynamic-map">
    <maplibre-map ref="map" :bounds="initialBounds" :max-zoom="15" :show-geolocate-control="true" @loaded="onMapLoaded" />
  </div>
</template>

<script>
import ajax from '../../helpers/ajax'
import { LngLatBounds } from 'maplibre-gl'
import MaplibreMap from '@/components/maplibre-map'
import { urlParamsWithNamespace, urlWithParams } from '../../helpers/url-params'
import {MIN_LATITUDE_EARTH, MAX_LATITUDE_EARTH, MIN_LONGITUDE_EARTH, MAX_LONGITUDE_EARTH} from '@/helpers/super-boundary-box'

const LOCATIONS_SOURCE_NAME = 'locations'
const MAP_LAYER_NAME_CLUSTERS = 'clusters'
const MAP_LAYER_NAME_CLUSTER_COUNTS = 'cluster-counts'
const MAP_LAYER_NAME_UNCLUSTERED_POINTS = 'unclustered-points'

export default {
  components: { MaplibreMap },

  props: {
    effortSlug: {
      type: String,
      required: true
    },
    extraUrlParams: {
      type: Object,
      required: false,
      default: () => {}
    },
    initialBounds: {
      type: Array,
      required: false,
      default: () => [{lat: MIN_LATITUDE_EARTH, lng: MIN_LONGITUDE_EARTH}, {lat: MAX_LATITUDE_EARTH, lng: MAX_LONGITUDE_EARTH}]
    }
  },

  data() {
    return {
      accentColor: null,
      bounds: null,
      loaded: false,
      locations: [],
      mapGl: null,
      secondaryAccentColor: null
    }
  },

  computed: {
    geoJsonPoints() {
      if (!this.locations) {
        return []
      }

      return {
        type: 'FeatureCollection',
        features: this.locations.map(l => ({
          type: 'Feature',
          properties: {
            name: l.query
          },
          geometry: {
            type: 'Point',
            coordinates: [l.longitude, l.latitude]
          }
        }))
      }
    },

    locationsUrl() {
      return `/efforts/${this.effortSlug}/locations`
    }
  },

  created() {
    const documentComputedStyle = getComputedStyle(document.documentElement)
    this.accentColor = documentComputedStyle.getPropertyValue('--accent-color')
    this.secondaryAccentColor = documentComputedStyle.getPropertyValue('--secondary-accent-color')
    this.clusterTextColor = documentComputedStyle.getPropertyValue('--text-on-secondary-accent-color')
  },

  mounted() {
    this.loadLocations()
  },

  methods: {
    fitBounds() {
      if (this.loaded && this.locations.length > 0) {
        const initialCoordinates = [this.locations[0].longitude, this.locations[0].latitude]
        const bounds = this.locations.reduce((bounds, location) => {
          return bounds.extend([location.longitude, location.latitude])
        }, new LngLatBounds(initialCoordinates, initialCoordinates))

        this.mapGl.fitBounds(bounds, {padding: 30})
      }
    },

    goToLocation(loc) {
      const params = {...urlParamsWithNamespace(loc, 'location'), ...this.extraUrlParams}
      window.location = urlWithParams(`/efforts/${this.effortSlug}/near`, params)
    },

    initializeMapPointsLayer() {
      this.mapGl.addSource(LOCATIONS_SOURCE_NAME, {
        type: 'geojson',
        data: this.geoJsonPoints,
        cluster: true,
        clusterMaxZoom: 14, // Max zoom to cluster points on
        clusterRadius: 50 // Radius of each cluster when clustering points (defaults to 50)
      })

      // Add a layer showing the places as clusters
      this.mapGl.addLayer({
        id: MAP_LAYER_NAME_CLUSTERS,
        type: 'circle',
        source: LOCATIONS_SOURCE_NAME,
        filter: ['has', 'point_count'],
        paint: {
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            20,
            100,
            30,
            750,
            40
          ],
          'circle-color': this.secondaryAccentColor,
          'circle-stroke-color': 'white',
          'circle-stroke-width': 2,
          'circle-opacity': 1
        }
      })

      // Add a layer showing the counts of the clusters
      this.mapGl.addLayer({
        id: MAP_LAYER_NAME_CLUSTER_COUNTS,
        type: 'symbol',
        source: LOCATIONS_SOURCE_NAME,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': ['get', 'point_count_abbreviated'],
          'text-font': ['Noto Sans Regular'],
          'text-size': 12
        },
        paint: {
          'text-color': this.clusterTextColor
        }
      })

      // Add a final layer for showing the unclustered points
      this.mapGl.addLayer({
        id: MAP_LAYER_NAME_UNCLUSTERED_POINTS,
        type: 'circle',
        source: LOCATIONS_SOURCE_NAME,
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-radius': 5,
          'circle-color': this.accentColor,
          'circle-stroke-color': 'white',
          'circle-stroke-width': 2,
          'circle-opacity': 1
        }
      })

      // inspect a cluster on click
      this.mapGl.on('click', MAP_LAYER_NAME_CLUSTERS, e => {
        const features = this.mapGl.queryRenderedFeatures(e.point, {
          layers: [MAP_LAYER_NAME_CLUSTERS]
        })
        const clusterId = features[0].properties.cluster_id
        this.mapGl.getSource(LOCATIONS_SOURCE_NAME).getClusterExpansionZoom(
          clusterId,
          (err, zoom) => {
            if (err) return

            this.mapGl.easeTo({
              center: features[0].geometry.coordinates,
              zoom
            })
          }
        )
      })

      // When a click event occurs on a feature in the unclustered-point layer,
      // navigate to the petition page for that location
      this.mapGl.on('click', MAP_LAYER_NAME_UNCLUSTERED_POINTS, e => {
        const coordinates = e.features[0].geometry.coordinates
        this.goToLocation({longitude: coordinates[0], latitude: coordinates[1]})
      })

      // Show a pointer cursor when hovering over clustered / unclustered markers
      const map = this.mapGl
      const layers = [MAP_LAYER_NAME_CLUSTERS, MAP_LAYER_NAME_UNCLUSTERED_POINTS]
      layers.forEach(layerName => {
        map.on('mousemove', layerName, () => {
          map.getCanvas().style.cursor = 'pointer'
        })
        map.on('mouseleave', layerName, () => {
          map.getCanvas().style.cursor = ''
        })
      })
    },

    onMapLoaded({map}) {
      this.loaded = true
      this.mapGl = map
      this.initializeMapPointsLayer()
      this.fitBounds()
    },

    loadLocations() {
      ajax.get({
        url: this.locationsUrl,
        successHandler: (resp) => {
          this.locations = resp
          this.loaded = true
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.dynamic-map {
  height: 420px;
  width: 100%;

  ::v-deep(img) {
    max-width: none;
  }
}
</style>
